import { StatusChip, type StatusChipColor } from "@/components/common/fields/StatusChipField";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { UIComponentSize, type ChipSize } from "@/types/shared/uiComponents";

/**
 * Where a claimant's effective specific deductible comes from: the server's
 * `effective_deductible_source` keys plus `family` (document review). One map,
 * so the claimant page, the policy's Lasered Claimants tab and the review
 * toolbar give a source the same label and colour (Feature #3502, UX review #10).
 * Warning is left to review states; contract keeps the secondary colour of
 * ContractLaserDeductibleChip.
 */
export const DEDUCTIBLE_SOURCE_META: Record<string, { label: string; color: StatusChipColor }> = {
  specific: { label: "Specific", color: "default" },
  family: { label: "Family", color: "default" },
  lasered: { label: "Lasered", color: "primary" },
  modification: { label: "Modification", color: "primary" },
  conditional_laser: { label: "Conditional", color: "info" },
  contract_laser: { label: "Contract", color: "secondary" },
  contract_excluded: { label: "Excluded", color: "error" },
  review_decision: { label: "Review decision", color: "warning" },
};

/** Label for a source key; "" when unknown. */
export const deductibleSourceLabel = (source?: string | null): string =>
  (source && DEDUCTIBLE_SOURCE_META[source]?.label) || "";

interface DeductibleSourceChipProps {
  /** Server source key (`effective_deductible_source`, or `family`). Unknown keys render nothing. */
  source?: string | null;
  /** When set, the chip reads "Lasered: $350,000.00". */
  amount?: number;
  size?: ChipSize;
}

/** Deductible-source badge, next to LaseredDeductibleChip. */
export const DeductibleSourceChip = ({ source, amount, size = UIComponentSize.Small }: DeductibleSourceChipProps) => {
  const meta = source ? DEDUCTIBLE_SOURCE_META[source] : undefined;
  if (!meta) return null;
  const label =
    typeof amount === "number" && !Number.isNaN(amount) ? `${meta.label}: ${formatUSD(amount)}` : meta.label;
  return <StatusChip label={label} color={meta.color} size={size} />;
};
